import React, { useCallback, useEffect, useRef } from 'react'
import cn from 'classnames'
import { isMobile } from 'lib/device'
import styles from './sidebar.module.scss'

export interface SidebarProps {
    children: React.ReactNode
    show: boolean
    onClose?: () => void
    containerClassName?: string
    openClassName?: string
    overlayClassName?: string
    contentClassName?: string
    mobileContentClassName?: string
    containerProps?: React.HTMLAttributes<HTMLDivElement>
    overlayProps?: React.HTMLAttributes<HTMLDivElement>
    contentProps?: React.HTMLAttributes<HTMLDivElement>
    closeOnOutsideClick?: boolean
    showOverlay?: boolean
}

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
    if (!ref) return
    if (typeof ref === 'function') {
        ref(value)
        return
    }

    try {
        ;(ref as React.MutableRefObject<T | null>).current = value
    } catch (error) {
        throw new Error(`Cannot assign value "${value}" to ref.`)
    }
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(function Sidebar(
    {
        children,
        show,
        onClose,
        containerClassName,
        openClassName,
        overlayClassName,
        contentClassName,
        mobileContentClassName,
        containerProps,
        overlayProps,
        contentProps,
        closeOnOutsideClick = true,
        showOverlay = true,
    },
    forwardedRef,
) {
    const contentRef = useRef<HTMLDivElement | null>(null)

    const setContentRef = useCallback(
        (node: HTMLDivElement | null) => {
            contentRef.current = node
            assignRef(forwardedRef, node)
        },
        [forwardedRef],
    )

    useEffect(() => {
        if (!show || !onClose || !closeOnOutsideClick) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node | null

            if (!target) return
            if (contentRef.current && !contentRef.current.contains(target)) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [show, onClose, closeOnOutsideClick])

    const { className: containerClassNameFromProps, ...restContainerProps } = containerProps ?? {}
    const { className: overlayClassNameFromProps, ...restOverlayProps } = overlayProps ?? {}
    const { className: contentClassNameFromProps, ...restContentProps } = contentProps ?? {}

    return (
        <div
            className={cn(
                styles.sidebarContainerBase,
                containerClassName,
                containerClassNameFromProps,
                show && openClassName,
            )}
            {...restContainerProps}
        >
            {showOverlay && (
                <div
                    className={cn(
                        styles.sidebarOverlayBase,
                        overlayClassName,
                        overlayClassNameFromProps,
                    )}
                    {...restOverlayProps}
                />
            )}
            <div
                ref={setContentRef}
                className={cn(
                    styles.sidebarContentBase,
                    contentClassName,
                    contentClassNameFromProps,
                    isMobile && mobileContentClassName,
                )}
                {...restContentProps}
            >
                {children}
            </div>
        </div>
    )
})

export default Sidebar
